import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text } from "./typography";
import { API_BASE_URL } from '../../lib/api';

/**
 * EditWithdrawalDialog - Dialog for editing an existing withdrawal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when withdrawal is updated successfully
 * @param {Object} props.withdrawal - The withdrawal to edit
 */
export function EditWithdrawalDialog({
  open,
  onOpenChange,
  onSuccess,
  withdrawal,
}) {
  const [formData, setFormData] = React.useState({
    amount: "",
    withdrawalDate: "",
    currency: "EUR",
    reason: "",
    notes: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const CURRENCIES = [
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "SEK", label: "SEK - Swedish Krona" },
    { value: "NOK", label: "NOK - Norwegian Krone" },
  ];

  const REASONS = [
    { value: "living_expenses", label: "Living Expenses" },
    { value: "emergency", label: "Emergency Fund" },
    { value: "major_purchase", label: "Major Purchase" },
    { value: "rebalancing", label: "Portfolio Rebalancing" },
    { value: "tax_payment", label: "Tax Payment" },
    { value: "other", label: "Other" },
  ];

  // Populate form when withdrawal changes
  React.useEffect(() => {
    if (open && withdrawal) {
      setFormData({
        amount: withdrawal.amount?.toString() || "",
        withdrawalDate: withdrawal.date || withdrawal.withdrawalDate || "",
        currency: withdrawal.currency || "EUR",
        reason: withdrawal.reason || "",
        notes: withdrawal.notes || "",
      });
      setError(null);
    }
  }, [open, withdrawal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      return "Amount must be positive";
    }
    if (!formData.withdrawalDate) {
      return "Withdrawal date is required";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        withdrawalDate: formData.withdrawalDate,
        currency: formData.currency,
        reason: formData.reason || "",
        notes: formData.notes || "",
      };

      await axios.put(
        `${API_BASE_URL}/transactions/withdrawals/${withdrawal.transactionId}`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to update withdrawal:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!withdrawal) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle>Edit Withdrawal</DialogTitle>
          <DialogDescription>
            Update the details of this withdrawal
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Amount and Currency */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Withdrawal Details
            </legend>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Amount *</Label>
                <Input
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="e.g., 1000.00"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleSelectChange("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Withdrawal Date *</Label>
                <Input
                  name="withdrawalDate"
                  type="date"
                  value={formData.withdrawalDate}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Reason</Label>
                <Select
                  value={formData.reason}
                  onValueChange={(value) => handleSelectChange("reason", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Notes</Label>
              <Input
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional notes about this withdrawal..."
              />
            </div>
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditWithdrawalDialog;
